import { Injectable } from '@angular/core';
import { CLIENT_TYPE_LIMITED_COMPANY } from '@app/shared/misc/constants';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { BlaCompany } from '../entity/bla-company';
import { Client } from '../entity/client';
import { CloudDbData } from '../entity/cloud-db-data';
import { LundifyConnection } from '../entity/lundify-connection';
import { Type } from '../entity/type';
import { catchErrorAndShowMessage } from '../misc/errorHandlers';
import { ToastActions } from '../state/toast/toast.actions';
import { CollaborationPackageParams, CollaborationPackageResponse } from '../state/types/collaboration-package.types';
import { BLService } from './bl.service';
import { ClientService } from './clients.service';

@Injectable({
  providedIn: 'root',
})
export class CloudCompanyService extends BLService {
  private RESOURCE_ENDPOINT = 'cloud-company';

  constructor(private clientService: ClientService) {
    super();
    this.useCache();
    this.useTranslation('CloudCompanyService');
  }

  getCloudCompanies(useDeepClientSearch = true, skipCache = false): Observable<BlaCompany[]> {
    if (!this.hasCacheExpired() && skipCache === false) {
      return this.getCacheObservable<BlaCompany[]>();
    }

    const param = useDeepClientSearch ? '?deep=true' : '';

    return this.httpGet<BlaCompany[]>(this.RESOURCE_ENDPOINT + param).pipe(
      tap((result) => skipCache || this.updateCache(result)), // don't update cache if "skipCache" is true
    );
  }

  // Users of the client's Lundify company - the candidates shown in the "Search person to invite"
  // field of the Shared documents share overlay. "id" is the cloudUserId expected by the BLA
  // share intake (docs/design/workflow-shared-documents-api.md, section 2).
  getLundifyCompanySubscriptionUsers(clientId: number): Observable<LundifyConnection[]> {
    return this.httpGet<LundifyConnection[]>(
      `${this.RESOURCE_ENDPOINT}/${clientId}/lundify/company-subscription/users`,
      this.httpAuthHeader.getHeaderWithoutBody(),
    );
  }

  getCollaborationCloudCompanies({
    responsibleId,
  }: CollaborationPackageParams): Observable<CollaborationPackageResponse> {
    const params = responsibleId ? `?userId=${responsibleId}` : '';

    return this.httpGet<CollaborationPackageResponse>(`${this.RESOURCE_ENDPOINT}/collaboration-package${params}`);
  }

  getCloudCompanyEmail({ clientId, cloudApiKey }: { clientId: number; cloudApiKey: string }): Observable<string> {
    return this.httpGet<{ email: string }>(
      `${this.RESOURCE_ENDPOINT}/email?clientId=${clientId || ''}&cloudApiKey=${cloudApiKey || ''}`,
    ).pipe(map((result) => result?.email || null));
  }

  getCloudCompanyVatAccountingInterval({ cloudApiKey }: { cloudApiKey: string }): Observable<string> {
    return this.httpGet<{ vatAccountingInterval: string }>(
      `${this.RESOURCE_ENDPOINT}/vatAccountingInterval?cloudApiKey=${cloudApiKey || ''}`,
    ).pipe(map((result) => result?.vatAccountingInterval || null));
  }

  updateCloudCompanyInformationByClientId(clientId: number) {
    // maps error codes to error codes that are used for this method
    const beforeErrorMessageProcessor = (errorCode: string) =>
      ({
        COUNTRY_TO_LONG: 'COUNTRY_TO_LONG_UPDATE',
        CLIENT_NAME_TO_LONG: 'CLIENT_NAME_TO_LONG_UPDATE',
        ADDRESS_TO_LONG: 'ADDRESS_TO_LONG_UPDATE',
      })[errorCode] || errorCode;

    return this.httpPut<Client>(
      `${this.RESOURCE_ENDPOINT}/${clientId}`,
      null,
      undefined,
      catchErrorAndShowMessage({ store: this.store, beforeErrorMessageProcessor }),
    );
  }

  exportClientToCloud(data: CloudDbData): Observable<{ cloudApiKey: string }> {
    // maps error codes to error codes that are used for this method
    const beforeErrorMessageProcessor = (errorCode: string) =>
      ({
        COUNTRY_TO_LONG: 'COUNTRY_TO_LONG_CREATE',
        CLIENT_NAME_TO_LONG: 'CLIENT_NAME_TO_LONG_CREATE',
        ADDRESS_TO_LONG: 'ADDRESS_TO_LONG_CREATE',
      })[errorCode] || errorCode;

    return this.httpPost<{ cloudApiKey: string }>(`${this.RESOURCE_ENDPOINT}/export`, data, undefined, (source) =>
      source.pipe(
        tap(() =>
          this.store.dispatch(
            ToastActions.showInfoMessage({
              summary: this.translate('saved'),
              detail: this.translate('newCloudDatabaseCreated'),
            }),
          ),
        ),
        catchErrorAndShowMessage({ store: this.store, beforeErrorMessageProcessor }),
      ),
    );
  }

  importCloudCompaniesToByst(
    selectedCloudCompanies: BlaCompany[],
  ): Observable<{ success: boolean; createdClients: Client[]; error?: string }> {
    return this.clientService.getClientsTypes().pipe(
      map((allCompanyTypes) =>
        selectedCloudCompanies.map((company) => this.mapCompanyToClient(company, allCompanyTypes)),
      ),
      switchMap((clients: Client[]) =>
        this.httpPost<{ success: boolean; createdClients: Client[]; error?: string }>(
          `${this.RESOURCE_ENDPOINT}/import`,
          clients,
        ),
      ),
      tap(() =>
        this.store.dispatch(
          ToastActions.showInfoMessage({
            summary: this.translate('saved'),
            detail: this.translate('clientIsImportedToFirmSupport'),
          }),
        ),
      ),
    );
  }

  archiveCloudCompany(cloudApiKey: string, amountOfYears: number) {
    return this.httpPut<void>(`${this.RESOURCE_ENDPOINT}/archive/${cloudApiKey}`, { amountOfYears });
  }

  unarchiveCloudCompany(cloudApiKey: string, data: { clientName: string; socialSecurityNumber: string }) {
    return this.httpPut<void>(`${this.RESOURCE_ENDPOINT}/unarchive/${cloudApiKey}`, data);
  }

  deleteCloudCompany(cloudApiKey: string) {
    return this.httpDelete(`${this.RESOURCE_ENDPOINT}/${cloudApiKey}`);
  }

  private mapCompanyToClient(company: BlaCompany, allCompanyTypes: Type[]) {
    const client = new Client(company.name);
    client.id = null;
    client.cloudApiKey = company.publicKey;
    client.corporateIdentity = company.orgNumber;
    client.type = this.getCompanyType(allCompanyTypes, company.companyType);
    client.email = company.email;
    client.phone = company.phone;
    client.address = company.box;
    client.zipCode = company.zip;
    client.city = company.city;
    client.residence = company.domicile;
    client.country = company.country;
    client.webpage = company.web;
    return client;
  }

  private getCompanyType(allCompanyTypes: Type[], cloudCompanyType: number) {
    // key = cloud company type number
    // value = byst name of the type
    const mapping: Record<number, string> = {
      1: CLIENT_TYPE_LIMITED_COMPANY,
      2: 'PARTNERSHIP',
      3: 'SOLE_PROPRIETORSHIP',
      4: CLIENT_TYPE_LIMITED_COMPANY,
      5: 'NON_PROFIT_ASSOCIATION',
    };

    const name = mapping[cloudCompanyType];

    return allCompanyTypes.find((type) => type.name === name);

    // AB = 1;
    // HB_KB = 2;
    // ENSKILD_FIRMA = 3;
    // EKONOMISK_FORENING = 4;
    // IDEELL_FORENING = 5;

    // PERSON('Privatperson'),
    // SOLE_PROPRIETORSHIP('Enskild firma'),
    // LIMITED_COMPANY('Aktiebolag/Ekonomisk förening'),
    // PARTNERSHIP('Handelsbolag/Kommanditbolag'),
    // NON_PROFIT_ASSOCIATION('Ideell förening/Stiftelse');
  }
}
